import { AnimatePresence, motion } from "framer-motion";
import { CSSProperties, useState } from "react";
import styles from "../WorkoutPlanDialog.module.scss";
import {
  DistanceUnit,
  IWorkoutPlanExerciseViewModel,
  WorkoutExerciseType,
  WorkoutSection,
} from "../models/workoutPlanViewModel";
import { Icon, IconType } from "../../../icons/Icon";
import { WorkoutPlanSetRow } from "./WorkoutPlanSetRow";
import { useExerciseNotes } from "../hooks/useExerciseNotes";
import { SkeletonItem } from "../../skeletons/SkeletonItem";
import { RestTimerPicker, formatRestTimerLabel } from "./RestTimerPicker";
import { SupersetPicker } from "./SupersetPicker";
import { ExerciseTypePicker, exerciseTypeLabel } from "./ExerciseTypePicker";
import { MuscleGroupPicker } from "./MuscleGroupPicker";
import { SectionPicker, workoutSectionLabel } from "./SectionPicker";
import { SetUpdateHandler } from "../hooks/useSetRow";
import {
  MuscleGroup,
  muscleGroupColor,
  muscleGroupLabel,
} from "../models/muscleGroups";

interface IWorkoutPlanExerciseCardProps {
  exercise: IWorkoutPlanExerciseViewModel;
  isReadOnly: boolean;
  onRemoveExercise: (planExerciseId: number) => Promise<void> | void;
  onUpdateNotes: (
    planExerciseId: number,
    notes: string,
  ) => Promise<void> | void;
  onUpdateRestTimer: (
    planExerciseId: number,
    restTimerSeconds: number,
  ) => Promise<void> | void;
  onUpdateUseAutomaticTimer: (
    planExerciseId: number,
    useAutomatic: boolean,
  ) => Promise<void> | void;
  onUpdateSupersetLetter: (
    planExerciseId: number,
    letter: string | null,
  ) => Promise<void> | void;
  onUpdateExerciseType: (
    planExerciseId: number,
    type: WorkoutExerciseType,
  ) => Promise<void> | void;
  onUpdateMuscleGroups: (
    planExerciseId: number,
    muscleGroups: MuscleGroup[],
  ) => Promise<void> | void;
  onUpdateResistanceBand?: (
    planExerciseId: number,
    isResistanceBand: boolean,
  ) => Promise<void> | void;
  onUpdateDistanceUnit?: (
    planExerciseId: number,
    distanceUnit: DistanceUnit,
  ) => Promise<void> | void;
  onUpdateSection?: (
    planExerciseId: number,
    section: WorkoutSection,
  ) => Promise<void> | void;
  /**
   * Offer the "Част" chip that moves the exercise between sections. Only once the
   * workout uses a warm-up or cool-down, so a plain workout looks as it always did.
   */
  showSectionChip?: boolean;
  onAddSet: (planExerciseId: number) => Promise<void> | void;
  onUpdateSet: SetUpdateHandler;
  onToggleSetCompleted: (
    setId: number,
    isCompleted: boolean,
  ) => Promise<void> | void;
  onRemoveSet: (setId: number) => Promise<void> | void;
}

interface ISetColumn {
  label: string;
  /** A time column holds two inputs (minutes : seconds), so it is a bit wider. */
  isTime?: boolean;
  /** The distance column, whose header switches metres / kilometres. */
  isDistance?: boolean;
}

/**
 * The value columns of the sets table, in the order the row renders its inputs:
 * the band resistance first (band exercises), then the type's own values.
 */
const setValueColumns = (
  type: WorkoutExerciseType,
  distanceUnit: DistanceUnit,
  isResistanceBand: boolean,
): ISetColumn[] => {
  const band: ISetColumn[] = isResistanceBand ? [{ label: "Ластик" }] : [];
  switch (type) {
    case WorkoutExerciseType.Time:
      return [...band, { label: "Време", isTime: true }];
    case WorkoutExerciseType.TimePace:
      return [...band, { label: "Време", isTime: true }, { label: "км/ч" }];
    case WorkoutExerciseType.Distance:
      return [
        ...band,
        { label: "Кг" },
        { label: distanceUnit === DistanceUnit.Kilometers ? "Км" : "Метри", isDistance: true },
      ];
    case WorkoutExerciseType.KgReps:
    default:
      return [...band, { label: "Кг" }, { label: "Повт." }];
  }
};

export const WorkoutPlanExerciseCard = ({
  exercise,
  isReadOnly,
  onRemoveExercise,
  onUpdateNotes,
  onUpdateRestTimer,
  onUpdateUseAutomaticTimer,
  onUpdateSupersetLetter,
  onUpdateExerciseType,
  onUpdateMuscleGroups,
  onUpdateResistanceBand,
  onUpdateDistanceUnit,
  onUpdateSection,
  showSectionChip = false,
  onAddSet,
  onUpdateSet,
  onToggleSetCompleted,
  onRemoveSet,
}: IWorkoutPlanExerciseCardProps) => {
  const { value: notesValue, handleChange: handleNotesChange } =
    useExerciseNotes({
      planExerciseId: exercise.id,
      initialNotes: exercise.notes,
      disabled: isReadOnly,
      onUpdate: onUpdateNotes,
    });

  // One slot, three possible pickers. Using a discriminated state keeps mutual
  // exclusivity automatic — opening one closes the others.
  const [openPicker, setOpenPicker] = useState<
    "rest" | "superset" | "type" | "muscle" | "section" | null
  >(null);

  const isPending = exercise.id < 0;
  const restTimerLabel = formatRestTimerLabel(exercise.restTimerSeconds);
  const isRestTimerOff = exercise.restTimerSeconds <= 0;
  const supersetLabel = exercise.supersetLetter ?? "—";
  const typeLabel = exerciseTypeLabel(exercise.exerciseType);
  const hasMuscleGroups = exercise.muscleGroups.length > 0;
  // System-catalog exercises have a fixed name, type and muscle groups — lock those two
  // editors even for admins/owners (the name isn't editable here anyway). The rest timer,
  // superset, notes and sets stay editable because they're per-workout, not per-exercise.
  const canEditType = !isReadOnly && !exercise.isSystemDefined;
  const canEditMuscles = !isReadOnly && !exercise.isSystemDefined;
  // The band option belongs to the exercise like its muscle groups, so it locks the same way.
  const canEditBand = !isReadOnly && !exercise.isSystemDefined && !!onUpdateResistanceBand;
  const canEditUnit = !isReadOnly && !!onUpdateDistanceUnit;
  const valueColumns = setValueColumns(
    exercise.exerciseType,
    exercise.distanceUnit,
    exercise.isResistanceBand,
  );
  const setsTableStyle = {
    "--sets-inputs": valueColumns
      .map((c) => (c.isTime ? "var(--sets-col-time)" : "var(--sets-col-input)"))
      .join(" "),
  } as CSSProperties;
  const toggleDistanceUnit = () => {
    if (isPending || !onUpdateDistanceUnit) return;
    void onUpdateDistanceUnit(
      exercise.id,
      exercise.distanceUnit === DistanceUnit.Kilometers
        ? DistanceUnit.Meters
        : DistanceUnit.Kilometers,
    );
  };

  return (
    <div className={styles.ex_card}>
      <div className={styles.ex_head}>
        <div className={styles.ex_left}>
          <div className={styles.ex_icon_wrap}>
            <Icon type={IconType.Dumbell} color="rgba(150,149,210,0.9)" />
          </div>
          <div className={styles.ex_name}>{exercise.exerciseName}</div>
        </div>
        {!isReadOnly && (
          <button
            type="button"
            className={styles.ex_del}
            onClick={() => onRemoveExercise(exercise.id)}
            aria-label="Премахни упражнение"
          >
            <Icon type={IconType.DeleteIcon} color="rgba(255,95,95,0.7)" />
          </button>
        )}
      </div>

      {(!isReadOnly || notesValue) && (
        <div className={styles.notes_row}>
          <textarea
            className={styles.notes_inp}
            rows={1}
            placeholder="Добави бележка..."
            value={notesValue}
            disabled={isReadOnly}
            onChange={(e) => handleNotesChange(e.target.value)}
          />
        </div>
      )}

      <div className={styles.chips_row}>
        {/* Rest timer — read-only mode locks the value to a static chip; the
            trainer's choice is what the client sees during the workout. */}
        {isReadOnly ? (
          <div className={styles.rest_chip_static}>
            <span className={styles.rest_chip_icon}>
              <Icon type={IconType.ClockIcon} color="rgba(150,149,210,0.95)" />
            </span>
            <span className={styles.rest_chip_label}>Почивка:</span>
            <span
              className={
                isRestTimerOff
                  ? styles.rest_chip_value_off
                  : styles.rest_chip_value
              }
            >
              {restTimerLabel}
            </span>
          </div>
        ) : (
          <button
            type="button"
            className={styles.rest_chip}
            onClick={() => !isPending && setOpenPicker("rest")}
            disabled={isPending}
            aria-label="Промени времето за почивка"
          >
            <span className={styles.rest_chip_icon}>
              <Icon type={IconType.ClockIcon} color="rgba(150,149,210,0.95)" />
            </span>
            <span className={styles.rest_chip_label}>Почивка:</span>
            <span
              className={
                isRestTimerOff
                  ? styles.rest_chip_value_off
                  : styles.rest_chip_value
              }
            >
              {restTimerLabel}
            </span>
          </button>
        )}

        {/* Superset letter — trainer-only chip. For the client we only echo
            the assignment when one exists; no chip if there's no superset. */}
        {!isReadOnly ? (
          <button
            type="button"
            className={styles.rest_chip}
            onClick={() => !isPending && setOpenPicker("superset")}
            disabled={isPending}
            aria-label="Промени буквата за суперсет"
          >
            <span className={styles.rest_chip_label}>Сет:</span>
            <span
              className={
                exercise.supersetLetter
                  ? styles.rest_chip_value
                  : styles.rest_chip_value_off
              }
            >
              {supersetLabel}
            </span>
          </button>
        ) : (
          exercise.supersetLetter && (
            <div className={styles.rest_chip_static}>
              <span className={styles.rest_chip_label}>Сет:</span>
              <span className={styles.rest_chip_value}>{supersetLabel}</span>
            </div>
          )
        )}

        {/* Exercise type — trainer chooses the measurement system. The client (and
            anyone viewing a locked system exercise) sees a static chip so they know
            what to expect (sec / kg×reps / m). */}
        {canEditType ? (
          <button
            type="button"
            className={styles.rest_chip}
            onClick={() => !isPending && setOpenPicker("type")}
            disabled={isPending}
            aria-label="Промени типа на упражнението"
          >
            <span className={styles.rest_chip_label}>Тип:</span>
            <span className={styles.rest_chip_value}>{typeLabel}</span>
          </button>
        ) : (
          <div className={styles.rest_chip_static}>
            <span className={styles.rest_chip_label}>Тип:</span>
            <span className={styles.rest_chip_value}>{typeLabel}</span>
          </div>
        )}

        {/* Resistance band — a switch on the exercise itself. Anyone who can't change
            it only sees the chip when the band is on. */}
        {canEditBand ? (
          <button
            type="button"
            className={styles.rest_chip}
            onClick={() => !isPending && onUpdateResistanceBand?.(exercise.id, !exercise.isResistanceBand)}
            disabled={isPending}
            aria-pressed={exercise.isResistanceBand}
            aria-label={exercise.isResistanceBand ? "Изключи ластика" : "Включи ластик"}
          >
            <span className={styles.rest_chip_label}>Ластик:</span>
            <span
              className={
                exercise.isResistanceBand
                  ? styles.rest_chip_value
                  : styles.rest_chip_value_off
              }
            >
              {exercise.isResistanceBand ? "Да" : "Не"}
            </span>
          </button>
        ) : (
          exercise.isResistanceBand && (
            <div className={styles.rest_chip_static}>
              <span className={styles.rest_chip_label}>Ластик</span>
            </div>
          )
        )}

        {showSectionChip && !isReadOnly && onUpdateSection && (
          <button
            type="button"
            className={styles.rest_chip}
            onClick={() => !isPending && setOpenPicker("section")}
            disabled={isPending}
            aria-label="Премести в друга част"
          >
            <span className={styles.rest_chip_label}>Част:</span>
            <span className={styles.rest_chip_value}>
              {workoutSectionLabel(exercise.section)}
            </span>
          </button>
        )}
      </div>

      {/* Muscle groups. For the trainer the tag row doubles as the editor entry
          point (tap anywhere to open the picker); for the client it's read-only
          info. Each tag carries a category-coloured dot that matches the bars in
          the per-workout muscle split. */}
      {!canEditMuscles
        ? hasMuscleGroups && (
            <div className={styles.muscle_row}>
              {exercise.muscleGroups.map((group) => (
                <span key={group} className={styles.muscle_tag}>
                  <span
                    className={styles.muscle_dot}
                    style={{ background: muscleGroupColor(group) }}
                  />
                  {muscleGroupLabel(group)}
                </span>
              ))}
            </div>
          )
        : hasMuscleGroups
          ? (
              <button
                type="button"
                className={`${styles.muscle_row} ${styles.muscle_row_btn}`}
                onClick={() => !isPending && setOpenPicker("muscle")}
                disabled={isPending}
                aria-label="Промени мускулните групи"
              >
                {exercise.muscleGroups.map((group) => (
                  <span key={group} className={styles.muscle_tag}>
                    <span
                      className={styles.muscle_dot}
                      style={{ background: muscleGroupColor(group) }}
                    />
                    {muscleGroupLabel(group)}
                  </span>
                ))}
                <span className={styles.muscle_edit} aria-hidden>
                  <Icon
                    type={IconType.EditIcon}
                    width={13}
                    height={13}
                    color="currentColor"
                  />
                </span>
              </button>
            )
          : (
              <div className={styles.muscle_row}>
                <button
                  type="button"
                  className={styles.muscle_add}
                  onClick={() => !isPending && setOpenPicker("muscle")}
                  disabled={isPending}
                >
                  + Мускулни групи
                </button>
              </div>
            )}

      {!isReadOnly && (
        <>
          <RestTimerPicker
            isOpen={openPicker === "rest"}
            currentSeconds={exercise.restTimerSeconds}
            useAutomaticTimer={exercise.useAutomaticTimer}
            onClose={() => setOpenPicker(null)}
            onPick={(seconds) => {
              if (isPending) return;
              void onUpdateRestTimer(exercise.id, seconds);
            }}
            onToggleAutomaticTimer={(value) => {
              if (isPending) return;
              void onUpdateUseAutomaticTimer(exercise.id, value);
            }}
          />

          <SupersetPicker
            isOpen={openPicker === "superset"}
            current={exercise.supersetLetter}
            onClose={() => setOpenPicker(null)}
            onPick={(letter) => {
              if (isPending) return;
              void onUpdateSupersetLetter(exercise.id, letter);
            }}
          />

          <ExerciseTypePicker
            isOpen={openPicker === "type"}
            current={exercise.exerciseType}
            onClose={() => setOpenPicker(null)}
            onPick={(type) => {
              if (isPending) return;
              void onUpdateExerciseType(exercise.id, type);
            }}
          />

          <MuscleGroupPicker
            isOpen={openPicker === "muscle"}
            current={exercise.muscleGroups}
            onClose={() => setOpenPicker(null)}
            onSave={(groups) => {
              if (isPending) return;
              void onUpdateMuscleGroups(exercise.id, groups);
            }}
          />

          {onUpdateSection && (
            <SectionPicker
              isOpen={openPicker === "section"}
              current={exercise.section}
              onClose={() => setOpenPicker(null)}
              onPick={(section) => {
                if (isPending) return;
                void onUpdateSection(exercise.id, section);
              }}
            />
          )}
        </>
      )}

      <div className={styles.sets_table} style={setsTableStyle}>
        <div className={styles.sets_head}>
          <span>Сет</span>
          <span>Предишна</span>
          {valueColumns.map((column) =>
            column.isDistance && canEditUnit ? (
              <span key={column.label}>
                <button
                  type="button"
                  className={styles.sets_head_unit}
                  onClick={toggleDistanceUnit}
                  disabled={isPending}
                  aria-label={
                    exercise.distanceUnit === DistanceUnit.Kilometers
                      ? "Въвеждай в метри"
                      : "Въвеждай в километри"
                  }
                >
                  {column.label}
                </button>
              </span>
            ) : (
              <span key={column.label}>{column.label}</span>
            ),
          )}
          <span>✓</span>
          <span />
        </div>
        {isPending ? (
          <div className={styles.sets_skeleton}>
            {[0, 1, 2].map((i) => (
              <div className={styles.sets_skeleton_row} key={i}>
                <SkeletonItem isLoading value="" width="100%" />
              </div>
            ))}
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {exercise.sets.map((set, idx) => (
              <motion.div
                key={set.clientKey ?? set.id}
                layout
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                style={{ overflow: "hidden" }}
              >
                <WorkoutPlanSetRow
                  set={set}
                  setIndex={idx}
                  isReadOnly={isReadOnly}
                  exerciseType={exercise.exerciseType}
                  distanceUnit={exercise.distanceUnit}
                  isResistanceBand={exercise.isResistanceBand}
                  supersetLetter={exercise.supersetLetter}
                  onUpdate={onUpdateSet}
                  onToggleCompleted={onToggleSetCompleted}
                  onRemove={onRemoveSet}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {!isReadOnly && (
        <button
          type="button"
          className={styles.add_set_btn}
          onClick={() => onAddSet(exercise.id)}
          disabled={isPending}
        >
          + Добави сет
        </button>
      )}
    </div>
  );
};
